import { GlassCard } from './GlassCard';
import { cn } from '@/lib/cn';
import { formatCurrency, formatNumber, formatPercent } from '@/lib/format';
import type { KpiCard as KpiCardType } from '@/types';

interface KpiCardProps {
  kpi: KpiCardType;
  trend?: { value: number; positive: boolean };
  className?: string;
}

const STATUS_BADGE = {
  GOOD: { class: 'bg-tertiary text-black', label: 'OPTIMAL' },
  WARN: { class: 'bg-secondary text-black', label: 'WARN' },
  BAD: { class: 'bg-error text-white', label: 'CRITICAL' },
  NEUTRAL: { class: 'bg-outline text-text-bright', label: 'INFO' },
} as const;

export function KpiCard({ kpi, trend, className }: KpiCardProps) {
  const fmt = (val: number) => {
    switch (kpi.format) {
      case 'currency':
        return formatCurrency(val);
      case 'percent':
        return formatPercent(val);
      default:
        return formatNumber(val);
    }
  };

  const badge = STATUS_BADGE[kpi.status];

  return (
    <GlassCard
      variant="kinetic"
      padding="md"
      rounded="none"
      interactive
      className={cn('relative', className)}
    >
      <div className="flex justify-between items-start mb-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
          {kpi.label}
        </p>
        {trend && (
          <span
            className={cn(
              'px-2 py-0.5 font-bold uppercase text-[10px] tracking-widest border-2',
              trend.positive
                ? 'bg-tertiary text-black border-black'
                : 'bg-error text-white border-error',
            )}
          >
            {trend.positive ? '▲' : '▼'} {formatPercent(Math.abs(trend.value), 0)}
          </span>
        )}
        {!trend && (
          <span
            className={cn(
              'px-2 py-0.5 font-bold uppercase text-[10px] tracking-widest border-2 border-black',
              badge.class,
            )}
          >
            {badge.label}
          </span>
        )}
      </div>
      <h3 className="font-condensed text-5xl text-text-bright num leading-none mb-2">
        {fmt(kpi.value)}
      </h3>
      {kpi.target && (
        <div className="mt-3 pt-3 border-t-2 border-outline flex justify-between items-center">
          <span className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
            target
          </span>
          <span className="font-mono text-xs text-text-bright num">
            {fmt(kpi.target)} {kpi.unit}
          </span>
        </div>
      )}
    </GlassCard>
  );
}
